import React, { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

export default function RHFFileField({ name, required = false, fileType, label = 'Click to upload', helperText }) {
  const { control, formState } = useFormContext();
  const { errors } = formState;
  const inputRef = useRef(null);

  const validateFileType = (file) => {
    if (!fileType) return true;
    if (Array.isArray(fileType)) return fileType.some((type) => file.name.endsWith(type));
    return file.name.endsWith(fileType);
  };

  const resetInput = () => {
    if (inputRef.current) inputRef.current.value = null;
  };

  // Callers pass types both as 'csv' and as '.csv'; prefixing a dot to the second made '..csv', which the
  // file dialog matches against nothing, so every file was greyed out and could not be chosen.
  const accept = fileType
    ? (Array.isArray(fileType) ? fileType : [fileType]).map((t) => `.${String(t).replace(/^\./, '')}`).join(',')
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? 'This field is required.' : false,
        validate: (files) => {
          if (files && files.length > 0 && !validateFileType(files[0])) {
            return `Invalid file type. Allowed: ${Array.isArray(fileType) ? fileType.join(', ') : fileType}`;
          }
          return true;
        },
      }}
      render={({ field }) => {
        const hasFile = field.value && field.value[0];
        const hasError = !!errors[name];

        return (
          <Box
            onClick={() => { resetInput(); inputRef.current.click(); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              px: '16px',
              py: '12px',
              border: `1.5px dashed ${hasError ? '#dc2626' : hasFile ? '#1565c0' : '#d1d5db'}`,
              borderRadius: '8px',
              bgcolor: hasError ? '#fef2f2' : hasFile ? '#eff6ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s',
              '&:hover': {
                borderColor: hasError ? '#dc2626' : '#1565c0',
                bgcolor: hasError ? '#fef2f2' : '#eff6ff',
              },
            }}
          >
            {hasFile
              ? <InsertDriveFileOutlinedIcon sx={{ fontSize: 26, color: '#1565c0', flexShrink: 0 }} />
              : <UploadFileOutlinedIcon sx={{ fontSize: 26, color: hasError ? '#dc2626' : '#9ca3af', flexShrink: 0 }} />
            }

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {hasFile ? (
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1565c0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {field.value[0].name}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '13px', color: hasError ? '#dc2626' : '#6b7280' }}>
                  {label}
                  {fileType && (
                    <Box component="span" sx={{ ml: '6px', fontSize: '11px', color: '#9ca3af' }}>
                      ({Array.isArray(fileType) ? fileType.join(', ') : fileType})
                    </Box>
                  )}
                </Typography>
              )}
              {hasError && (
                <Typography sx={{ fontSize: '11px', color: '#dc2626', mt: '3px' }}>
                  {errors[name].message}
                </Typography>
              )}
              {!hasError && helperText && (
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', mt: '3px' }}>
                  {helperText}
                </Typography>
              )}
            </Box>

            <input
              onChange={(e) => field.onChange(e.target.files)}
              type="file"
              accept={accept}
              ref={inputRef}
              hidden
            />
          </Box>
        );
      }}
    />
  );
}
